class Calculator extends HTMLElement {
  constructor() {
    super();

    this.type = '';
    this.open = false;

    this.length = 0;
    this.width = 0;
    this.walls = [];
    this.openings = [];

    this.cornersInner = 0;
    this.cornersOuter = 0;

    this.stripLength = 2.5;
    this.wastePercent = 10;

    this.accMode = 'auto'; // 'auto' | 'manual'
    this.accManual = { endcaps: 0, connectors: 0 };

    this.onClick = this.onClick.bind(this);
    this.onOutside = this.onOutside.bind(this);
    this.onInput = this.onInput.bind(this);
  }

  // translations helper (z window.theme.baseboardCalculatorStrings)
  t(key, fallback = '') {
    return (window.theme?.baseboardCalculatorStrings && window.theme.baseboardCalculatorStrings[key]) || fallback;
  }

  connectedCallback() {
    this.cache();

    this.atcBtn = this.querySelector('[data-calc-atc]');
    this.atcText = this.querySelector('[data-calc-atc-text]');
    this.errorBox = this.querySelector('[data-calc-error]');

    this.bind();
    this.initTooltips();
    this.sync();

    this.setAtcEnabled(false);
    this.updateAtcLabel();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutside);
  }

  cache() {
    this.mainMenu = this.querySelector('[data-dropdown-menu]');
    this.mainLabel = this.querySelector('[data-dropdown-label]');
    this.hiddenInput = this.querySelector('[data-room-type-input]');

    this.stripLengthEl = this.querySelector('[data-strip-length]');
    this.wastePercentEl = this.querySelector('[data-waste-percent]');

    this.panels = Array.from(this.querySelectorAll('[data-panel]'));

    this.wallsWrap = this.querySelector('[data-walls]');
    this.wallTpl = this.querySelector('template[data-wall-template]');

    this.openingTpl = this.querySelector('template[data-opening-template]');
    this.openingsSumEls = Array.from(this.querySelectorAll('[data-openings-sum]'));

    this.cornersSumEls = Array.from(this.querySelectorAll('[data-corners-sum]'));

    this.perimeterValueEls = Array.from(this.querySelectorAll('[data-perimeter-value]'));
    this.netPerimeterEls = Array.from(this.querySelectorAll('[data-net-perimeter]'));
    this.metersWithWasteEls = Array.from(this.querySelectorAll('[data-meters-with-waste]'));
    this.stripsCountEls = Array.from(this.querySelectorAll('[data-strips-count]'));

    // packs UI (AKCESORIA)
    this.connectorPacksCountEls = Array.from(this.querySelectorAll('[data-connector-packs-count]'));
    this.connectorPackSizeEls = Array.from(this.querySelectorAll('[data-connector-pack-size]'));

    this.endcapPacksCountEls = Array.from(this.querySelectorAll('[data-endcap-packs-count]'));
    this.endcapPackSizeEls = Array.from(this.querySelectorAll('[data-endcap-pack-size]'));

    this.cornerInnerPacksCountEls = Array.from(this.querySelectorAll('[data-corner-inner-packs-count]'));
    this.cornerInnerPackSizeEls = Array.from(this.querySelectorAll('[data-corner-inner-pack-size]'));

    this.cornerOuterPacksCountEls = Array.from(this.querySelectorAll('[data-corner-outer-packs-count]'));
    this.cornerOuterPackSizeEls = Array.from(this.querySelectorAll('[data-corner-outer-pack-size]'));

    // accessories UI
    this.accEndcapsEls = Array.from(this.querySelectorAll('[data-acc-endcaps]'));
    this.accConnectorsEls = Array.from(this.querySelectorAll('[data-acc-connectors]'));
    this.accCornersDecorInnerEls = Array.from(this.querySelectorAll('[data-acc-corners-decor-inner]'));
    this.accCornersDecorOuterEls = Array.from(this.querySelectorAll('[data-acc-corners-decor-outer]'));

    this.accModeBtns = Array.from(this.querySelectorAll('[data-acc-mode-btn]'));
    this.accModeInput = this.querySelector('[data-acc-mode-input]');
    this.accAutoEls = Array.from(this.querySelectorAll('[data-acc-auto]'));
    this.accManualBlocks = Array.from(this.querySelectorAll('[data-acc-manual]'));

    if (this.stripLengthEl) {
      this.stripLengthLocked = this.stripLengthEl.dataset.locked === 'true';
      this.stripLength = this.toNumber(this.stripLengthEl.value) || 2.5;
    }

    if (this.wastePercentEl) {
      this.wastePercent = this.toInt(this.wastePercentEl.value);
    }
  }

  bind() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.addEventListener('change', this.onInput);
    document.addEventListener('click', this.onOutside);
  }

  // ----------------- ERROR UI -----------------
  hideErrorMessage() {
    if (!this.errorBox) return;
    this.errorBox.textContent = '';
    this.errorBox.classList.add('hidden');
  }

  showErrorMessage(message) {
    if (!this.errorBox) {
      console.warn('[calculator] Missing error box element');
      return;
    }

    this.errorBox.textContent = message || this.t('errorGeneric', 'Wystąpił błąd.');
    this.errorBox.classList.remove('hidden');
  }

  // ----------------- TOOLTIP -----------------
  initTooltips() {
    this.waitForTippy(() => {
      const nodes = this.querySelectorAll('[data-tooltip]');
      if (!nodes.length) return;

      nodes.forEach(el => {
        if (el._tippy) return;

        const content = el.getAttribute('data-tooltip-content') || '';
        if (!content) return;

        tippy(el, {
          content,
          allowHTML: true,
          animation: 'scale',
          theme: 'light-border',
          interactive: true,
          placement: 'top',
          trigger: 'mouseenter focus',
        });
      });
    });
  }

  waitForTippy(cb) {
    const start = Date.now();
    const tick = () => {
      if (window.tippy && window.Popper) {
        cb();
        return;
      }
      if (Date.now() - start > 3000) return;
      requestAnimationFrame(tick);
    };
    tick();
  }

  // ----------------- TYPE & MODE -----------------
  setType(nextType) {
    if (this.type === nextType) return;
    this.type = nextType;

    if (nextType === 'irregular' && this.walls.length === 0) {
      this.addWallRow();
    }

    this.sync();
  }

  setAccessoriesMode(mode) {
    if (mode !== 'auto' && mode !== 'manual') return;
    if (this.accMode === mode) return;

    this.accMode = mode;

    this.accModeBtns.forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.mode === mode);
    });

    const isManual = mode === 'manual';
    this.accManualBlocks.forEach(block => (block.hidden = !isManual));
    this.accAutoEls.forEach(el => (el.hidden = isManual));

    if (this.accModeInput) this.accModeInput.value = mode;

    this.hideErrorMessage();
    this.updateFinalUI();
  }

  // ----------------- SYNC -----------------
  sync() {
    if (this.mainLabel) {
      this.mainLabel.textContent =
        this.type === 'rectangle'
          ? this.t('roomTypeRectangle', 'Prostokąt')
          : this.type === 'irregular'
            ? this.t('roomTypeIrregular', 'Nieregularny')
            : this.t('roomTypePlaceholder', 'Wybierz typ pokoju');
    }

    if (this.hiddenInput) this.hiddenInput.value = this.type;

    this.panels.forEach(panel => {
      panel.hidden = panel.dataset.panel !== this.type;
    });

    if (this.mainMenu) this.mainMenu.hidden = !this.open;

    this.setAccessoriesMode(this.accMode);
    this.updateUI();
  }

  updateUI() {
    this.updateOpeningsSumUI();
    this.updateCornersUI();
    this.updatePerimeterUI();
    this.updateFinalUI();
  }

  // ----------------- EVENTS -----------------
  onOutside(e) {
    if (!this.contains(e.target) && this.open) {
      this.open = false;
      this.sync();
    }
  }

  onClick(e) {
    const mainToggle = e.target.closest('[data-dropdown-toggle]');
    const mainOption = e.target.closest('[data-value]');

    if (mainToggle) {
      this.open = !this.open;
      this.sync();
      return;
    }

    if (mainOption) {
      this.open = false;
      this.setType(mainOption.dataset.value);
      this.hideErrorMessage();
      return;
    }

    const modeBtn = e.target.closest('[data-acc-mode-btn]');
    if (modeBtn) {
      this.setAccessoriesMode(modeBtn.dataset.mode);
      return;
    }

    if (e.target.closest('[data-add-wall]')) {
      this.addWallRow();
      this.hideErrorMessage();
      this.updateUI();
      return;
    }

    const removeWallBtn = e.target.closest('[data-remove-wall]');
    if (removeWallBtn) {
      const row = removeWallBtn.closest('.calc-wall-row');
      const idx = row ? Number(row.dataset.index) : -1;
      if (idx >= 0) {
        this.removeWallRow(idx);
        this.hideErrorMessage();
        this.updateUI();
      }
      return;
    }

    if (e.target.closest('[data-add-opening]')) {
      this.addOpeningRow();
      this.hideErrorMessage();
      this.updateUI();
      return;
    }

    const atc = e.target.closest('[data-calc-atc]');
    if (atc) {
      if (atc.getAttribute('aria-disabled') === 'true') return;

      const strips = this.calculateStripsCount();
      if (strips <= 0) return;

      this.addToCart();
      return;
    }

    const removeOpeningBtn = e.target.closest('[data-remove-opening]');
    if (removeOpeningBtn) {
      const row = removeOpeningBtn.closest('.calc-opening-row');
      const idx = row ? Number(row.dataset.index) : -1;
      if (idx >= 0) {
        this.removeOpeningRow(idx);
        this.hideErrorMessage();
        this.updateUI();
      }
      return;
    }
  }

  onInput(e) {
    this.hideErrorMessage();

    if (e.target.matches('[data-opening-type-select]')) {
      const row = e.target.closest('.calc-opening-row');
      const idx = row ? Number(row.dataset.index) : -1;
      const value = e.target.value;

      if (idx >= 0 && this.openings[idx]) this.openings[idx].type = value;

      this.syncOpeningEndcapVisibility(row, value);
      this.updateUI();
      return;
    }

    if (e.target.matches('[data-strip-length]')) {
      if (this.stripLengthLocked) return;
      this.stripLength = Math.max(0, this.toNumber(e.target.value));
      this.updateUI();
      return;
    }

    if (e.target.matches('[data-waste-percent]')) {
      this.wastePercent = Math.max(0, this.toInt(e.target.value));
      this.updateUI();
      return;
    }

    if (e.target.matches('[data-length]')) {
      this.length = this.toNumber(e.target.value);
      this.updateUI();
      return;
    }

    if (e.target.matches('[data-width]')) {
      this.width = this.toNumber(e.target.value);
      this.updateUI();
      return;
    }

    if (e.target.matches('[data-corners-inner]')) {
      this.cornersInner = this.toInt(e.target.value);
      this.updateUI();
      return;
    }

    if (e.target.matches('[data-corners-outer]')) {
      this.cornersOuter = this.toInt(e.target.value);
      this.updateUI();
      return;
    }

    if (e.target.matches('[data-wall-length]')) {
      const row = e.target.closest('.calc-wall-row');
      const idx = row ? Number(row.dataset.index) : -1;
      if (idx >= 0) this.walls[idx] = this.toNumber(e.target.value);
      this.updateUI();
      return;
    }

    if (e.target.matches('[data-opening-width]')) {
      const row = e.target.closest('.calc-opening-row');
      const idx = row ? Number(row.dataset.index) : -1;
      if (idx >= 0 && this.openings[idx]) this.openings[idx].width = this.toNumber(e.target.value);
      this.updateUI();
      return;
    }

    if (e.target.matches('[data-opening-endcap]')) {
      const row = e.target.closest('.calc-opening-row');
      const idx = row ? Number(row.dataset.index) : -1;
      if (idx >= 0 && this.openings[idx]) this.openings[idx].endcap = !!e.target.checked;
      this.updateUI();
      return;
    }

    if (e.target.matches('[data-acc-manual-endcaps]')) {
      this.accManual.endcaps = this.toInt(e.target.value);
      this.updateFinalUI();
      return;
    }
    if (e.target.matches('[data-acc-manual-connectors]')) {
      this.accManual.connectors = this.toInt(e.target.value);
      this.updateFinalUI();
      return;
    }
  }

  syncOpeningEndcapVisibility(row, type) {
    const wrap = row?.querySelector('[data-opening-endcap-wrap]');
    const checkbox = row?.querySelector('[data-opening-endcap]');
    if (!wrap || !checkbox) return;

    const isDoor = type === 'drzwi';
    wrap.hidden = !isDoor;

    if (!isDoor) {
      checkbox.checked = false;
      const idx = Number(row.dataset.index);
      if (this.openings[idx]) this.openings[idx].endcap = false;
    }
  }

  // ----------------- ROWS -----------------
  addWallRow() {
    if (!this.wallTpl || !this.wallsWrap) return;

    const node = this.wallTpl.content.firstElementChild.cloneNode(true);
    const idx = this.walls.length;

    node.dataset.index = String(idx);
    this.walls.push(0);

    this.wallsWrap.appendChild(node);
  }

  removeWallRow(idx) {
    this.walls.splice(idx, 1);

    const rows = Array.from(this.wallsWrap.querySelectorAll('.calc-wall-row'));
    const rowToRemove = rows.find(r => Number(r.dataset.index) === idx);
    if (rowToRemove) rowToRemove.remove();

    Array.from(this.wallsWrap.querySelectorAll('.calc-wall-row')).forEach((row, i) => {
      row.dataset.index = String(i);
    });
  }

  getActiveOpeningsWrap() {
    const activePanel = this.panels.find(p => !p.hidden);
    return activePanel ? activePanel.querySelector('[data-openings]') : null;
  }

  addOpeningRow() {
    const wrap = this.getActiveOpeningsWrap();
    if (!wrap || !this.openingTpl) return;

    const node = this.openingTpl.content.firstElementChild.cloneNode(true);
    const idx = this.openings.length;

    node.dataset.index = String(idx);
    this.openings.push({ type: 'drzwi', width: 0, endcap: false });

    wrap.appendChild(node);
    this.initTooltips();
    this.syncOpeningEndcapVisibility(node, 'drzwi');
  }

  removeOpeningRow(idx) {
    this.openings.splice(idx, 1);

    Array.from(this.querySelectorAll('.calc-opening-row')).forEach(row => {
      if (Number(row.dataset.index) === idx) row.remove();
    });

    Array.from(this.querySelectorAll('.calc-opening-row')).forEach((row, i) => {
      row.dataset.index = String(i);
    });
  }

  // ----------------- CALC -----------------
  calculatePerimeter() {
    if (this.type === 'rectangle') return 2 * (this.length + this.width);
    if (this.type === 'irregular')
      return this.walls.reduce((sum, v) => sum + (Number.isFinite(v) ? v : 0), 0);
    return 0;
  }

  calculateOpeningsSum() {
    return this.openings.reduce((sum, o) => sum + (o?.width || 0), 0);
  }

  calculateNetPerimeter() {
    return Math.max(0, this.calculatePerimeter() - this.calculateOpeningsSum());
  }

  calculateMetersWithWaste() {
    return this.calculateNetPerimeter() * (1 + this.wastePercent / 100);
  }

  calculateStripsCount() {
    if (!this.stripLength || this.stripLength <= 0) return 0;
    return Math.ceil(this.calculateMetersWithWaste() / this.stripLength);
  }

  calculateAccessoriesAuto() {
    const selectedDoorEndcaps = this.openings.reduce((sum, o) => {
      const isDoor = o?.type === 'drzwi';
      const selected = !!o?.endcap;
      return sum + (isDoor && selected ? 1 : 0);
    }, 0);

    const endcaps = selectedDoorEndcaps * 2;
    const meters = this.calculateMetersWithWaste();
    const connectors = Math.ceil(meters / 2);

    return { endcaps, connectors };
  }

  getAccessoriesFinal() {
    if (this.accMode === 'manual') {
      return {
        endcaps: this.accManual.endcaps || 0,
        connectors: this.accManual.connectors || 0,
      };
    }
    return this.calculateAccessoriesAuto();
  }

  getCornersDecorInner() {
    return this.cornersInner || 0;
  }
  getCornersDecorOuter() {
    return this.cornersOuter || 0;
  }

  // ----------------- PACKS (AKCESORIA) -----------------
  get connectorPackSize() {
    return this.getPackSizeFromDataset('connectorPackText');
  }
  get endcapPackSize() {
    return this.getPackSizeFromDataset('endcapPackText');
  }
  get cornerInnerPackSize() {
    return this.getPackSizeFromDataset('cornerInnerPackText');
  }
  get cornerOuterPackSize() {
    return this.getPackSizeFromDataset('cornerOuterPackText');
  }

  getPackSizeFromDataset(key) {
    const raw = this.dataset[key] || '';
    const n = this.parsePackSize(raw);
    return n > 0 ? n : 1;
  }

  parsePackSize(input) {
    const str = String(input || '').trim();
    if (!str) return 0;

    const match = str.match(/(\d+(?:[.,]\d+)?)/);
    if (!match) return 0;

    const num = parseFloat(match[1].replace(',', '.'));
    if (!Number.isFinite(num) || num <= 0) return 0;

    return Math.floor(num);
  }

  ceilDiv(a, b) {
    const A = Number(a) || 0;
    const B = Number(b) || 1;
    if (B <= 0) return 0;
    return Math.ceil(A / B);
  }

  // ----------------- AVAILABILITY (z dataset ATC button) -----------------
  get variantId() {
    return this.dataset.variantId || null;
  }
  get connectorVariantId() {
    return this.dataset.connectorVariantId || null;
  }
  get endcapVariantId() {
    return this.dataset.endcapVariantId || null;
  }
  get cornerInnerVariantId() {
    return this.dataset.cornerInnerVariantId || null;
  }
  get cornerOuterVariantId() {
    return this.dataset.cornerOuterVariantId || null;
  }

  getAvailFromDataset(key, fallback = true) {
    const raw = this.atcBtn?.dataset[key];
    if (raw === undefined) return fallback;
    return String(raw) === 'true';
  }

  get stripAvailable() {
    return this.getAvailFromDataset('variantAvailable', true);
  }
  get connectorAvailable() {
    return this.getAvailFromDataset('connectorAvailable', true);
  }
  get endcapAvailable() {
    return this.getAvailFromDataset('endcapAvailable', true);
  }
  get cornerInnerAvailable() {
    return this.getAvailFromDataset('cornerInnerAvailable', true);
  }
  get cornerOuterAvailable() {
    return this.getAvailFromDataset('cornerOuterAvailable', true);
  }

  getNeededItemsStatus() {
    const strips = this.calculateStripsCount();
    const acc = this.getAccessoriesFinal();

    // listwy = SZTUKI (strips), a akcesoria = opakowania
    const connectorPacks = this.ceilDiv(acc.connectors, this.connectorPackSize);
    const endcapPacks = this.ceilDiv(acc.endcaps, this.endcapPackSize);
    const cornerInnerPacks = this.ceilDiv(this.cornersInner, this.cornerInnerPackSize);
    const cornerOuterPacks = this.ceilDiv(this.cornersOuter, this.cornerOuterPackSize);

    const needed = {
      strip: strips > 0,
      connector: !!this.connectorVariantId && connectorPacks > 0,
      endcap: !!this.endcapVariantId && endcapPacks > 0,
      cornerInner: !!this.cornerInnerVariantId && cornerInnerPacks > 0,
      cornerOuter: !!this.cornerOuterVariantId && cornerOuterPacks > 0,
    };

    const missing = [];
    if (needed.strip && !this.stripAvailable) missing.push('strip');
    if (needed.connector && !this.connectorAvailable) missing.push('connector');
    if (needed.endcap && !this.endcapAvailable) missing.push('endcap');
    if (needed.cornerInner && !this.cornerInnerAvailable) missing.push('cornerInner');
    if (needed.cornerOuter && !this.cornerOuterAvailable) missing.push('cornerOuter');

    return { needed, missing };
  }

  getAvailabilityMessage(missingKeys) {
    if (!missingKeys?.length) return '';

    const labelMap = {
      strip: this.t('labelStrips', 'Listwy'),
      connector: this.t('labelConnectors', 'Łączniki'),
      endcap: this.t('labelEndcaps', 'Zakończenia'),
      cornerInner: this.t('labelCornerInner', 'Narożniki wewnętrzne'),
      cornerOuter: this.t('labelCornerOuter', 'Narożniki zewnętrzne'),
    };

    const names = missingKeys.map(k => labelMap[k] || k).join(', ');
    const tpl = this.t(
      'errorSoldOutItems',
      'Wyprzedane elementy: [items]. Zmień konfigurację lub wybierz inne warianty.'
    );
    return tpl.replace('[items]', names);
  }

  // ----------------- UI -----------------
  updateOpeningsSumUI() {
    const sum = this.formatNumber(this.calculateOpeningsSum());
    this.openingsSumEls.forEach(el => (el.textContent = sum));
  }

  updateCornersUI() {
    const total = (this.cornersInner || 0) + (this.cornersOuter || 0);
    this.cornersSumEls.forEach(el => (el.textContent = String(total)));
  }

  updatePerimeterUI() {
    const perimeter = this.formatNumber(this.calculatePerimeter());
    this.perimeterValueEls.forEach(el => (el.textContent = perimeter));
  }

  updateFinalUI() {
    const net = this.formatNumber(this.calculateNetPerimeter());
    const metersWithWaste = this.formatNumber(this.calculateMetersWithWaste());

    const strips = this.calculateStripsCount();
    const acc = this.getAccessoriesFinal();

    const connectorPacks = this.ceilDiv(acc.connectors, this.connectorPackSize);
    const endcapPacks = this.ceilDiv(acc.endcaps, this.endcapPackSize);
    const cornerInnerPacks = this.ceilDiv(this.cornersInner, this.cornerInnerPackSize);
    const cornerOuterPacks = this.ceilDiv(this.cornersOuter, this.cornerOuterPackSize);

    this.netPerimeterEls.forEach(el => (el.textContent = net));
    this.metersWithWasteEls.forEach(el => (el.textContent = metersWithWaste));
    this.stripsCountEls.forEach(el => (el.textContent = String(strips)));

    this.connectorPacksCountEls.forEach(el => (el.textContent = String(connectorPacks)));
    this.connectorPackSizeEls.forEach(el => (el.textContent = String(this.connectorPackSize)));

    this.endcapPacksCountEls.forEach(el => (el.textContent = String(endcapPacks)));
    this.endcapPackSizeEls.forEach(el => (el.textContent = String(this.endcapPackSize)));

    this.cornerInnerPacksCountEls.forEach(el => (el.textContent = String(cornerInnerPacks)));
    this.cornerInnerPackSizeEls.forEach(el => (el.textContent = String(this.cornerInnerPackSize)));

    this.cornerOuterPacksCountEls.forEach(el => (el.textContent = String(cornerOuterPacks)));
    this.cornerOuterPackSizeEls.forEach(el => (el.textContent = String(this.cornerOuterPackSize)));

    this.accEndcapsEls.forEach(el => (el.textContent = String(acc.endcaps)));
    this.accConnectorsEls.forEach(el => (el.textContent = String(acc.connectors)));

    const decorInner = this.getCornersDecorInner();
    const decorOuter = this.getCornersDecorOuter();

    this.accCornersDecorInnerEls.forEach(el => (el.textContent = String(decorInner)));
    this.accCornersDecorOuterEls.forEach(el => (el.textContent = String(decorOuter)));

    this.syncAtcState();
  }

  syncAtcState() {
    const strips = this.calculateStripsCount();
    const canCompute = strips > 0;

    const { missing } = this.getNeededItemsStatus();
    if (missing.length) {
      this.setAtcEnabled(false);
      if (this.atcText) this.atcText.textContent = this.t('soldOut', 'Wyprzedane');
      this.showErrorMessage(this.getAvailabilityMessage(missing));
      return;
    }

    if (!canCompute) {
      this.setAtcEnabled(false);
      this.updateAtcLabel();
      return;
    }

    this.setAtcEnabled(true);
    this.updateAtcLabel();
  }

  updateAtcLabel() {
    if (!this.atcBtn || !this.atcText) return;

    const strips = this.calculateStripsCount();
    if (!strips || strips <= 0) {
      this.atcText.textContent = this.t('addToCart', 'Dodaj do koszyka');
      return;
    }

    if (!this.stripAvailable) {
      this.atcText.textContent = this.t('soldOut', 'Wyprzedane');
      return;
    }

    const acc = this.getAccessoriesFinal();

    const connectorPacks = this.ceilDiv(acc.connectors, this.connectorPackSize);
    const endcapPacks = this.ceilDiv(acc.endcaps, this.endcapPackSize);
    const cornerInnerPacks = this.ceilDiv(this.cornersInner, this.cornerInnerPackSize);
    const cornerOuterPacks = this.ceilDiv(this.cornersOuter, this.cornerOuterPackSize);

    const stripPrice = parseInt(this.atcBtn.dataset.variantPrice || '0', 10);
    const connectorPrice = parseInt(this.atcBtn.dataset.connectorPrice || '0', 10);
    const endcapPrice = parseInt(this.atcBtn.dataset.endcapPrice || '0', 10);
    const cornerInnerPrice = parseInt(this.atcBtn.dataset.cornerInnerPrice || '0', 10);
    const cornerOuterPrice = parseInt(this.atcBtn.dataset.cornerOuterPrice || '0', 10);

    const total =
      stripPrice * strips +
      connectorPrice * connectorPacks +
      endcapPrice * endcapPacks +
      cornerInnerPrice * cornerInnerPacks +
      cornerOuterPrice * cornerOuterPacks;

    if (!total || total <= 0) {
      const tpl = this.t('addToCartWithStrips', 'Dodaj do koszyka ([qty] szt.)');
      this.atcText.textContent = tpl.replace('[qty]', String(strips));
      return;
    }

    this.atcText.textContent = `${this.t('addToCart', 'Dodaj do koszyka')} - ${this.formatMoney(total)}`;
  }

  // ----------------- ATC -----------------
  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  setAtcEnabled(enabled) {
    const btn = this.atcBtn || this.querySelector('[data-calc-atc]');
    if (!btn) return;

    btn.disabled = !enabled;
    btn.toggleAttribute('aria-disabled', !enabled);
  }

  async addToCart() {
    const stripVariantId = this.variantId;
    if (!stripVariantId) {
      console.warn('[calculator] Missing data-variant-id');
      return;
    }

    const { missing } = this.getNeededItemsStatus();
    if (missing.length) {
      this.setAtcEnabled(false);
      if (this.atcText) this.atcText.textContent = this.t('soldOut', 'Wyprzedane');
      this.showErrorMessage(this.getAvailabilityMessage(missing));
      return;
    }

    const strips = this.calculateStripsCount();
    const acc = this.getAccessoriesFinal();

    // listwy = SZTUKI
    const connectorPacks = this.ceilDiv(acc.connectors, this.connectorPackSize);
    const endcapPacks = this.ceilDiv(acc.endcaps, this.endcapPackSize);
    const cornerInnerPacks = this.ceilDiv(this.cornersInner, this.cornerInnerPackSize);
    const cornerOuterPacks = this.ceilDiv(this.cornersOuter, this.cornerOuterPackSize);

    const items = [{ id: String(stripVariantId), quantity: Number(strips) }];

    const connectorVariantId = this.connectorVariantId;
    if (connectorVariantId && connectorPacks > 0)
      items.push({ id: String(connectorVariantId), quantity: Number(connectorPacks) });

    const endcapVariantId = this.endcapVariantId;
    if (endcapVariantId && endcapPacks > 0)
      items.push({ id: String(endcapVariantId), quantity: Number(endcapPacks) });

    const cornerInnerVariantId = this.cornerInnerVariantId;
    if (cornerInnerVariantId && cornerInnerPacks > 0)
      items.push({ id: String(cornerInnerVariantId), quantity: Number(cornerInnerPacks) });

    const cornerOuterVariantId = this.cornerOuterVariantId;
    if (cornerOuterVariantId && cornerOuterPacks > 0)
      items.push({ id: String(cornerOuterVariantId), quantity: Number(cornerOuterPacks) });

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = theme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.headers['Content-Type'] = 'application/json';

    config.body = JSON.stringify({
      items,
      sections: sectionsToBundle,
      sections_url: window.location.pathname,
    });

    const btn = this.atcBtn || this.querySelector('[data-calc-atc]');
    btn?.setAttribute('aria-busy', 'true');
    btn?.setAttribute('aria-disabled', 'true');
    if (btn) btn.disabled = true;

    try {
      const res = await fetch(theme.routes.cart_add_url, config);
      const parsedState = await res.json();

      if (parsedState.status) {
        console.warn('[calculator] add error:', parsedState.description || parsedState.message);
        this.showErrorMessage(
          parsedState.description ||
            parsedState.message ||
            this.t('errorAddToCart', 'Wystąpił błąd podczas dodawania do koszyka.')
        );
        return;
      }

      const cartJson = await (
        await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })
      ).json();
      cartJson.sections = parsedState.sections;

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'calculator',
        productVariantId: String(stripVariantId),
        cart: cartJson,
      });

      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));

      this.cartDrawer?.show(btn || this);
    } catch (err) {
      console.error(err);
      this.showErrorMessage(this.t('errorNetwork', 'Błąd sieci. Spróbuj ponownie.'));
    } finally {
      btn?.removeAttribute('aria-busy');
      this.syncAtcState();
    }
  }

  // ----------------- utils -----------------
  toNumber(v) {
    const n = parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  }

  toInt(v) {
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  formatNumber(n) {
    return (Math.round(n * 100) / 100).toFixed(2);
  }

  formatMoney(cents) {
    const format = this.atcBtn?.dataset.moneyFormat || '{{amount}}';
    const value = (cents / 100).toFixed(2);

    if (format.includes('{{amount_no_decimals}}')) {
      return format.replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
    }
    if (format.includes('{{amount_with_comma_separator}}')) {
      return format.replace('{{amount_with_comma_separator}}', value.replace('.', ','));
    }
    if (format.includes('{{amount}}')) {
      return format.replace('{{amount}}', value);
    }

    return value;
  }
}

if (!customElements.get('calculator-component')) {
  customElements.define('calculator-component', Calculator);
}